import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AdminInfoResponse } from "@services/admin/types";

export interface EnvInfo {
  deploymentEnv: string;
  isProd: boolean;
  availableEnvironments: Array<{
    name: string;
    label: string;
    url: string;
    isProd: boolean;
  }>;
}

interface SystemStore {
  adminInfo: AdminInfoResponse | null;
  envInfo: EnvInfo | null;
  loading: boolean;
  envLoading: boolean;
  error: string | null;
  envError: string | null;
}

export const initialState: SystemStore = {
  adminInfo: null,
  envInfo: null,
  loading: false,
  envLoading: false,
  error: null,
  envError: null,
};

const slice = createSlice({
  name: "systemData",
  initialState,
  reducers: {
    setAdminInfoLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
      if (payload) {
        state.error = null;
      }
    },
    setAdminInfo: (state, { payload }: PayloadAction<AdminInfoResponse>) => {
      state.adminInfo = payload;
      state.loading = false;
      state.error = null;
    },
    setAdminInfoError: (state, { payload }: PayloadAction<string>) => {
      state.error = payload;
      state.loading = false;
    },
    clearAdminInfo: (state) => {
      state.adminInfo = null;
      state.loading = false;
      state.error = null;
    },
    setEnvInfoLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.envLoading = payload;
      if (payload) {
        state.envError = null;
      }
    },
    setEnvInfo: (state, { payload }: PayloadAction<EnvInfo>) => {
      state.envInfo = payload;
      state.envLoading = false;
      state.envError = null;
    },
    setEnvInfoError: (state, { payload }: PayloadAction<string>) => {
      state.envError = payload;
      state.envLoading = false;
    },
    clearEnvInfo: (state) => {
      state.envInfo = null;
      state.envLoading = false;
      state.envError = null;
    },
  },
});

export const {
  setAdminInfoLoading,
  setAdminInfo,
  setAdminInfoError,
  clearAdminInfo,
  setEnvInfoLoading,
  setEnvInfo,
  setEnvInfoError,
  clearEnvInfo,
} = slice.actions;

// Re-export thunks for convenience
export { fetchAdminInfo, fetchEnvInfo } from "./systemDataThunks";

// Re-export selectors for convenience
export {
  selectAdminInfo,
  selectAdminInfoLoading,
  selectAdminInfoError,
  selectEnvInfo,
  selectEnvInfoLoading,
  selectEnvInfoError,
  selectIsProduction,
  selectDeploymentEnv,
  selectAvailableEnvironments,
} from "./systemDataSelectors";

export default slice.reducer;